/**
 * Подбор со склада перед закупкой (СК-12–15): что ищем → похожие позиции на складах компании с оценкой
 * совпадения и объяснением → «Взять» (резерв под заявку и черновик перемещения на склад проекта).
 * Открывается с «Остатков» и по ссылке из заявки на закуп: /warehouse?find=…&unit=…&qty=…&line=…&project=…
 */
import type {
  SourcingCandidate,
  SourcingResult,
  SourcingTakeInput,
  SourcingTakeResult,
} from '@epc/contracts';
import { WAREHOUSE_KINDS } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../lib/api';
import { projectsQuery } from '../../lib/projects';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { parseNum, qty, warehousesQuery } from './queries';

export type SourcingPreset = {
  name?: string;
  unit?: string;
  quantity?: string;
  projectId?: string;
  requestLineId?: string;
};

function scoreTone(score: number): 'success' | 'info' | 'warning' | 'neutral' {
  if (score >= 100) return 'success';
  if (score >= 90) return 'info';
  if (score >= 70) return 'warning';
  return 'neutral';
}

export function SourcingDialog({
  open,
  onOpenChange,
  preset,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  preset?: SourcingPreset;
}) {
  const qc = useQueryClient();
  const projects = useQuery(projectsQuery());
  const warehouses = useQuery(warehousesQuery);
  // Свои склады (кладовщик): взять можно сразу, с чужих — по согласию владельца склада
  const operable = new Set((warehouses.data ?? []).filter((w) => w.canOperate).map((w) => w.id));
  const [f, setF] = useState({
    name: preset?.name ?? '',
    unit: preset?.unit ?? '',
    quantity: preset?.quantity ?? '',
    projectId: preset?.projectId ?? '',
  });
  const [probe, setProbe] = useState<SourcingPreset | null>(
    preset?.name || preset?.requestLineId ? preset : null,
  );
  const [taken, setTaken] = useState<SourcingTakeResult | null>(null);
  const result = useQuery({
    queryKey: ['wh', 'sourcing', probe],
    enabled: Boolean(probe && (probe.name || probe.requestLineId)),
    queryFn: () =>
      api<SourcingResult>('/warehouse/sourcing', {
        query: {
          name: probe?.requestLineId ? undefined : probe?.name,
          unit: probe?.unit || undefined,
          quantity:
            probe?.quantity && parseNum(probe.quantity) > 0 ? String(parseNum(probe.quantity)) : undefined,
          projectId: probe?.projectId || undefined,
          requestLineId: probe?.requestLineId,
        },
      }),
  });
  const data = result.data;
  const need = data?.probe.quantity ?? null;
  const left = need !== null && data ? Math.max(0, need - data.reservedForLine) : null;
  const search = () =>
    setProbe({
      name: f.name.trim(),
      unit: f.unit.trim(),
      quantity: f.quantity,
      projectId: f.projectId,
      requestLineId: preset?.requestLineId,
    });
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Подобрать со склада"
      description="Перед закупкой: та же или похожая позиция на складах компании, в пути и у подрядчиков. Оценка совпадения: 100 — тот же код, 90–99 — тот же класс и свойства, 70–89 — отличается одно свойство (СК-13)"
      width="lg"
      footer={
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          Закрыть
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {data?.probe.requestNumber ? (
          <p className="text-[13px] text-ink-2">
            Строка заявки на закуп № {data.probe.requestNumber}: {data.probe.name}
            {data.probe.unit ? `, ${data.probe.unit}` : ''}
            {need !== null ? `, нужно ${qty(need)}` : ''}
            {data.reservedForLine > 0 ? `, уже взято со склада ${qty(data.reservedForLine)}` : ''}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_120px_120px]">
            <Field label="Что нужно">
              <Input
                value={f.name}
                placeholder="Название позиции: Труба ПЭ100 SDR17 110"
                onChange={(e) => setF({ ...f, name: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && f.name.trim().length >= 2) search();
                }}
              />
            </Field>
            <Field label="Единица">
              <Input value={f.unit} placeholder="м" onChange={(e) => setF({ ...f, unit: e.target.value })} />
            </Field>
            <Field label="Количество">
              <Input
                inputMode="decimal"
                value={f.quantity}
                onChange={(e) => setF({ ...f, quantity: e.target.value })}
              />
            </Field>
            <Field label="Для проекта" className="sm:col-span-2">
              <Select value={f.projectId} onChange={(e) => setF({ ...f, projectId: e.target.value })}>
                <option value="">Не указан</option>
                {(projects.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} · {p.name}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="flex items-end">
              <Button
                variant="primary"
                className="w-full"
                disabled={f.name.trim().length < 2}
                loading={result.isFetching}
                onClick={search}
              >
                Найти
              </Button>
            </div>
          </div>
        )}
        {taken ? (
          <div className="rounded-md border border-success/40 bg-success/5 p-3 text-[13px]">
            {taken.status === 'requested' ? (
              <>
                Запрос отправлен владельцу склада (кладовщику, МОЛ или руководителю его проекта): после
                согласия резерв встанет и появится черновик перемещения. Запросы и решения — на вкладке{' '}
                <Link to="/warehouse" search={{ tab: 'reservations' }} className="underline">
                  «Резервы»
                </Link>
                .
              </>
            ) : null}
            {taken.status === 'active' ? 'Резерв поставлен. ' : null}
            {taken.status === 'active' && taken.documentId ? (
              <>
                Черновик перемещения{' '}
                <Link
                  to="/warehouse/documents/$docId"
                  params={{ docId: taken.documentId }}
                  className="underline"
                >
                  {taken.documentNumber}
                </Link>{' '}
                — проведёт кладовщик склада-отправителя, резерв переедет на склад проекта.
              </>
            ) : taken.status === 'active' ? (
              'Позиция на складе проекта — перемещение не нужно.'
            ) : null}
            {taken.note ? ` ${taken.note}.` : ''}
            {taken.substitutionRemembered ? ' Замена запомнена и будет предложена в следующих заявках.' : ''}
          </div>
        ) : null}
        {!probe ? null : result.isPending ? (
          <Spinner />
        ) : result.isError ? (
          <ErrorBox error={result.error} />
        ) : !data?.candidates.length ? (
          <EmptyState title="На складах ничего похожего нет">
            Закупка нужна. Если позиция есть в каталоге, но остатка нет — тоже нечего взять.
          </EmptyState>
        ) : (
          <ul className="flex flex-col gap-3">
            {data.candidates.map((c) => (
              <Candidate
                key={c.item.id}
                operable={operable}
                c={c}
                probe={data.probe}
                left={left}
                onTaken={(t) => {
                  setTaken(t);
                  qc.invalidateQueries({ queryKey: ['wh'] });
                  toast('Взято со склада');
                }}
              />
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}

function Candidate({
  operable,
  c,
  probe,
  left,
  onTaken,
}: {
  operable: Set<string>;
  c: SourcingCandidate;
  probe: SourcingResult['probe'];
  left: number | null;
  onTaken: (t: SourcingTakeResult) => void;
}) {
  const [qtyBy, setQtyBy] = useState<Record<string, string>>({});
  // Ключ формы: повторная отправка (двойное нажатие, обрыв связи) не ставит второй резерв
  const [formKey, setFormKey] = useState(() => crypto.randomUUID());
  const m = useMutation({
    mutationFn: (p: { warehouseId: string; quantity: number }) => {
      const body: SourcingTakeInput = {
        clientKey: `take-${formKey}-${p.warehouseId.slice(0, 8)}`,
        itemId: c.item.id,
        warehouseId: p.warehouseId,
        quantity: p.quantity,
        projectId: probe.projectId ?? undefined,
        requestLineId: probe.requestLineId ?? undefined,
        name: probe.name,
        unit: probe.unit ?? undefined,
        score: c.score,
        why: c.why,
      };
      return api<SourcingTakeResult>('/warehouse/sourcing/take', { body });
    },
    onSuccess: (t) => {
      setFormKey(crypto.randomUUID());
      onTaken(t);
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Не удалось взять', 'danger'),
  });
  return (
    <li className="rounded-md border border-line p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={scoreTone(c.score)}>{c.score} %</Badge>
        <Link
          to="/warehouse/items/$itemId"
          params={{ itemId: c.item.id }}
          className="inline-flex min-h-11 items-center font-medium underline"
        >
          {c.item.code} · {c.item.name}
        </Link>
        <span className="text-[13px] text-ink-2">{c.item.unit}</span>
        {c.rememberedUses > 0 ? (
          <Badge tone="accent">
            замену принимали {c.rememberedUses} {c.rememberedUses === 1 ? 'раз' : 'раза'}
          </Badge>
        ) : null}
      </div>
      <p className="mt-1 text-[13px] text-ink-2">
        {c.why}
        {c.score < 100
          ? ' — замену подтверждает ПТО; если меняется проектное решение, нужен ответ проектировщика'
          : ''}
      </p>
      <ul className="mt-2 flex flex-col gap-2">
        {c.places.map((p) => {
          // По умолчанию — сколько осталось по строке заявки, не больше доступного; всё взято — пусто
          const def = left === null ? p.available : Math.min(p.available, left);
          const v = qtyBy[p.warehouseId] ?? (def > 0 ? String(def) : '');
          const n = parseNum(v);
          const over = left !== null && n > left + 1e-9;
          return (
            <li
              key={p.warehouseId}
              className="flex flex-wrap items-center gap-2 rounded bg-surface-2 px-2 py-1.5 text-[13px]"
            >
              <span className="min-w-0 flex-1">
                <Link
                  to="/warehouse/warehouses/$warehouseId"
                  params={{ warehouseId: p.warehouseId }}
                  className="inline-flex min-h-11 items-center underline"
                >
                  {p.warehouseName}
                </Link>
                {p.projectCode ? ` · ${p.projectCode}` : ''}
                <span className="text-ink-2"> · {WAREHOUSE_KINDS[p.kind]}</span>
                {p.sameProject ? (
                  <Badge tone="success" className="ml-1">
                    склад проекта
                  </Badge>
                ) : null}
                <span className="block text-ink-2">
                  остаток {qty(p.onHand)}, доступно {qty(p.available)}
                </span>
              </span>
              {p.canTake ? (
                <>
                  <Input
                    inputMode="decimal"
                    aria-label="Сколько взять"
                    className="h-11 w-24"
                    value={v}
                    onChange={(e) => setQtyBy({ ...qtyBy, [p.warehouseId]: e.target.value })}
                  />
                  <Button
                    variant="primary"
                    className="h-11"
                    loading={m.isPending}
                    disabled={!(n > 0) || n > p.available + 1e-9 || over}
                    onClick={() => m.mutate({ warehouseId: p.warehouseId, quantity: n })}
                  >
                    {p.sameProject || !probe.projectId || operable.has(p.warehouseId)
                      ? 'Взять'
                      : 'Запросить у владельца склада'}
                  </Button>
                </>
              ) : (
                <span className="text-ink-3">
                  {p.kind === 'transit'
                    ? 'в пути'
                    : p.kind === 'contractor'
                      ? 'у подрядчика'
                      : 'нет права взять'}
                </span>
              )}
            </li>
          );
        })}
        {!c.places.length ? <li className="text-[13px] text-ink-3">Остатка на складах нет</li> : null}
      </ul>
    </li>
  );
}
